/**
 * Header component - Main navigation and branding
 * Handles responsive menu, theme toggle, language switch, CV download and secret admin access
 * Logos chargés dynamiquement depuis Supabase
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as styles from './header.css';
import { createPortal } from 'react-dom';
import { MdMenu, MdClose, MdFileDownload } from 'react-icons/md';

import { ThemeToggle } from "../../../shared/components/ThemeToggle/ThemeToggle";
import { LanguageToggle } from "../../../shared/components/LanguageToggle/LanguageToggle";
import { useLanguage } from '../../../../hooks';
import { apiService } from './../../../shared/services/apiService';
import { isServicesEnabled } from '../../../shared/services/featureFlags';
// Importer les logos statiques via Vite (génère des URLs fingerprintées valides en prod)
import logoMain from '../../../../assets/Logos/webcraft.svg';
import logoResponsive from '../../../../assets/Logos/webcraftresponsive.svg';
import logoResponsiveLight from '../../../../assets/Logos/webcraftresponsivelight.svg';

// ================================
// CONSTANTES
// ================================

// ✅ Valeurs par défaut si aucune URL Supabase n'est configurée côté About
const DEFAULT_CV_PDF = {
  fr: '/assets/download/CV Jean-François CHAUSSOY Développeur Web full stack.pdf',
  en: '/assets/download/CV_Jean-Francois_CHAUSSOY_EN.pdf'
} as const;

const ADMIN_CLICK_COUNT = 5;
const ADMIN_TIMEOUT = 3000;
const MOBILE_BREAKPOINT = 768;

// ================================
// INTERFACES
// ================================

interface HeaderProps {
  onLogoClick?: () => void;
}

// ================================
// ÉLÉMENTS DE navigation
// ================================

// Éléments de navigation principaux (base)
const baseNavigationItems = [
  { href: '#about', key: 'menu.about' },
  { href: '#skills', key: 'menu.skills' },
  { href: '#projects', key: 'menu.projects' },
  { href: '#education', key: 'menu.education' },
  { href: '#experience', key: 'menu.experience' },
  { href: 'mailto:jf.c.webcraft@gmail.com', key: 'menu.contact' }
];

/**
 * Header component
 * Manages desktop and mobile menu display, CV download link and secret admin access.
 * Logos chargés dynamiquement depuis Supabase avec fallbacks intelligents.
 */
export const Header: React.FC<HeaderProps> = ({ onLogoClick }) => {
  // ================================
  // HOOKS PRINCIPAUX
  // ================================

  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [navItems, setNavItems] = React.useState(baseNavigationItems);
  // URLs CV dynamiques (chargées depuis About si disponibles)
  const [cvUrls, setCvUrls] = React.useState<{ fr: string; en: string }>({
    fr: DEFAULT_CV_PDF.fr,
    en: DEFAULT_CV_PDF.en,
  });

  // ================================
  // ÉTAT LOCAL
  // ================================

  // State du menu mobile
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);

  // State responsive
  const [isMobile, setIsMobile] = React.useState<boolean>(
    window.innerWidth < MOBILE_BREAKPOINT
  );
  
  // State du thème
  const [isLightTheme, setIsLightTheme] = React.useState<boolean>(
    document.documentElement.classList.contains('light-theme')
  );
  // ✅ NOUVEAUX ÉTATS pour logos dynamiques
  const [logoUrls, setLogoUrls] = React.useState<{
    main: string;
    responsive: string;
    responsiveLight: string;
  }>({
    main: logoMain,
    responsive: logoResponsive,
    responsiveLight: logoResponsiveLight
  });
  const [logosLoading, setLogosLoading] = React.useState(true);

  // (Déplacé vers About): états d'accès admin secret ne sont plus utilisés ici
  const [clickTimeout, setClickTimeout] = React.useState<NodeJS.Timeout | null>(null);

  // ================================
  // REFS
  // ================================

  const menuRef = React.useRef<HTMLDivElement>(null);
  const burgerRef = React.useRef<HTMLButtonElement>(null);

  // ================================
  // CALLBACKS POUR LES APPELS API AVEC CACHE/DEDUP
  // ================================

  // Charger le flag Services pour afficher le lien (via cache TTL)
  const updateServicesLink = React.useCallback(async () => {
    try {
      const enabled = await isServicesEnabled();
      setNavItems(enabled ? [...baseNavigationItems, { href: '/services', key: 'menu.services' }] : baseNavigationItems);
    } catch {
      setNavItems(baseNavigationItems);
    }
  }, []);

  // Charger les URLs de CV depuis About (via cache TTL)
  const loadCvUrls = React.useCallback(async () => {
    try {
      const res = await apiService.get('/about', true, {
        cacheTtlMs: 180000, // 3 minutes cache
        useCache: true
      });
      if (res?.data) {
        const fr = (res.data as any).cv_url_fr;
        const en = (res.data as any).cv_url_en;
        setCvUrls({
          fr: typeof fr === 'string' && fr.length > 0 ? fr : DEFAULT_CV_PDF.fr,
          en: typeof en === 'string' && en.length > 0 ? en : DEFAULT_CV_PDF.en,
        });
      }
    } catch {}
  }, []);

  // Charger les logos depuis Supabase (via cache TTL)
  const loadLogos = React.useCallback(async () => {
    try {
      setLogosLoading(true);

      // ✅ correction - Utiliser getAll avec cache TTL automatique
      const assetsResponse = await apiService.get('/assets', true, {
        cacheTtlMs: 300000, // 5 minutes cache pour les assets
        useCache: true
      });

      if (assetsResponse?.data && Array.isArray(assetsResponse.data)) {
        const logos = assetsResponse.data.filter((asset: any) =>
          asset.type === 'logo' && asset.name?.includes('webcraft')
        );
        const newLogoUrls = { ...logoUrls };

        // Rechercher chaque logo par name
        const mainLogo = logos.find((logo: any) =>
          logo.name === 'webcraft-main' || logo.name?.includes('webcraft-main')
        );
        const responsiveLogo = logos.find((logo: any) =>
          logo.name === 'webcraft-responsive' && !logo.name?.includes('light')
        );
        const responsiveLightLogo = logos.find((logo: any) =>
          logo.name === 'webcraft-responsive-light' || logo.name?.includes('light')
        );
        // ✅ VALIDATION DES URLs - Éviter les erreurs 404 
        const isValidUrl = (url: string): boolean => {
          if (!url || url.trim() === '') return false;
          // Rejeter les URLs qui pointent vers src/ (non valides en production)
          if (url.includes('src/') || url.includes('\\src\\')) return false;
          // Rejeter les URLs relatives invalides
          if (url.startsWith('/images/') && !url.startsWith('/assets/')) return false;
          return true;
        };

        if (mainLogo?.url && isValidUrl(mainLogo.url)) {
          newLogoUrls.main = mainLogo.url;
        }
        if (responsiveLogo?.url && isValidUrl(responsiveLogo.url)) {
          newLogoUrls.responsive = responsiveLogo.url;
        }
        if (responsiveLightLogo?.url && isValidUrl(responsiveLightLogo.url)) {
          newLogoUrls.responsiveLight = responsiveLightLogo.url;
        }
        setLogoUrls(newLogoUrls);
      } else {
        console.warn('Unable to fetch logos from API, using static fallbacks');
      }

    } catch (error) {
      console.warn('Failed to load logos from API, using static fallbacks:', error);
    } finally {
      setLogosLoading(false);
    }
  }, []); // ✅ CORRECTION - Dépendances vides pour éviter les boucles infinies

  // ================================
  // EFFETS D'INITIALISATION AVEC PROTECTION ANTI-BOUCLES
  // ================================

  // Chargement initial une seule fois (ref pour éviter les doubles appels)
  const initialLoadRef = React.useRef(false);
  
  React.useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    
    // Appels API avec cache/dedup automatique
    updateServicesLink();
    loadCvUrls();
    loadLogos();

    // ✅ CORRECTION : Écouter les changements de feature flags
    const handleFlagsUpdate = () => {
      updateServicesLink();
    };

    window.addEventListener('featureFlagsUpdated', handleFlagsUpdate);
    
    return () => {
      window.removeEventListener('featureFlagsUpdated', handleFlagsUpdate);
    };
  }, []); // ✅ CORRECTION URGENTE - Dépendances vides, protection par ref

  // ================================
  // AUTRES EFFECTS
  // ================================

  // Observer les changements de thème
  React.useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.documentElement.classList.contains('light-theme'));
    };

    checkTheme();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkTheme();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Vérifier la taille d'écran au loading et lors du redimensionnement
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Nettoyer le timeout au démounting du component
  React.useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  // Fermer le menu mobile en cliquant à l'extérieur
  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        burgerRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !burgerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMenuOpen]);

  // Empêcher le défilement du body quand le menu mobile est ouvert
  React.useEffect(() => {
    document.body.style.overflow = (isMenuOpen && window.innerWidth < MOBILE_BREAKPOINT) ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================

  /**
   * ✅ NOUVELLE FONCTION - Sélectionner le logo approprié selon le thème et la taille d'écran
   */
  const getCurrentLogo = (): string => {
    // Logos dynamiques depuis Supabase (avec fallback sur statiques)
    if (isMobile) {
      if (isLightTheme && logoUrls.responsiveLight && logoUrls.responsiveLight !== '') {
        return logoUrls.responsiveLight;
      }
      if (logoUrls.responsive && logoUrls.responsive !== '') {
        return logoUrls.responsive;
      }
      // Fallback statique pour mobile
      return isLightTheme ? logoResponsiveLight : logoResponsive;
    } else {
      if (logoUrls.main && logoUrls.main !== '') {
        return logoUrls.main;
      }
      // Fallback statique pour desktop
      return logoMain;
    }
  };

  // Construire une URL orientée "download" si possible (supabase)
  const toDownloadUrl = (url: string, desiredFileName?: string): string => {
    if (!url) return url;
    // Si c'est une URL Supabase Storage, ajouter le paramètre download
    try {
      const u = new URL(url);
      const isSupabase = u.hostname.includes('supabase.co');
      if (isSupabase) {
        // Si un nom de fichier est fourni, l'utiliser (améliore le nom du fichier côté client)
        if (desiredFileName && desiredFileName.trim()) {
          u.searchParams.set('download', desiredFileName.trim());
        } else if (!u.searchParams.has('download')) {
          u.searchParams.set('download', '1');
        }
        return u.toString();
      }
      return url;
    } catch {
      return url;
    }
  };

  // Récupérer l'URL du CV par langue avec fallback
  const getCvUrlFor = (lang: 'fr' | 'en'): string => {
    const raw = (cvUrls[lang] || DEFAULT_CV_PDF[lang]);
    const filename = lang === 'en'
      ? 'CV_Jean-Francois_CHAUSSOY_EN.pdf'
      : 'CV_Jean-Francois_CHAUSSOY_Developpeur_Web_Full_Stack.pdf';
    return toDownloadUrl(raw, filename);
  };

  // ================================
  // GESTIONNAIRES D'ÉVÉNEMENTS
  // ================================

  /**
   * Gérer les erreurs de chargement de logo
   */
  const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Fallback vers les logos statiques
    if (isMobile) {
      e.currentTarget.src = isLightTheme ? logoResponsiveLight : logoResponsive;
    } else {
      e.currentTarget.src = logoMain;
    }
  };

  // Au clic sur le logo: revenir à l'accueil (aucun effet/accès admin ici)
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onLogoClick) {
      onLogoClick();
      return;
    }
    navigate('/');
  };

  // ✅ CORRIGÉ : Gérer le téléloading du CV
  // Plus de gestion programmatique: on laisse le navigateur gérer le téléchargement

  // Fermer le menu mobile
  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  // ================================
  // COMPOSANTS DE navigation
  // ================================

  /**
   * ✅ CORRIGÉ : Navigation desktop avec liens séparés et boutons d'action
   */
  const renderDesktopNavigation = () => {
  // URLs CV FR/EN (Supabase si dispo)
  const cvUrlFr = getCvUrlFor('fr');
  const cvUrlEn = getCvUrlFor('en');

    return (
      <div className={styles.desktopNavigation}>
        {/* Liens de navigation */}
        <ul className={styles.navLinks}>
          {navItems.map(({ href, key }) => (
            <li key={key}>
              {href.startsWith('/') ? (
                <a 
                  href={href} 
                  className={styles.menuItem}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(href);
                  }}
                >
                  {t(key)}
                </a>
              ) : (
                <a 
                  href={href} 
                  className={styles.menuItem}
                  {...(href.startsWith('mailto:') ? { target: '_self' } : {})}
                >
                  {t(key)}
                </a>
              )}
            </li>
          ))}
        </ul>

        {/* Boutons d'action */}
        <div className={styles.actionButtons}>
          <ThemeToggle />
          <LanguageToggle />
          {language === 'en' ? (
            <a
              href={cvUrlEn}
              download
              className={styles.downloadButton}
              rel="noopener noreferrer"
              aria-label={t('menu.downloadCV')}
              title={t('menu.downloadCV')}
            >
              <MdFileDownload size="1.2rem" aria-hidden="true" />
              Resume
            </a>
          ) : (
            <a
              href={cvUrlFr}
              download
              className={styles.downloadButton}
              rel="noopener noreferrer"
              aria-label={t('menu.downloadCV')}
              title={t('menu.downloadCV')}
            >
              <MdFileDownload size="1.2rem" aria-hidden="true" />
              CV
            </a>
          )}
        </div>
      </div>
    );
  };

  /**
   * ✅ CORRIGÉ : Menu mobile complet
   */
  const renderMobileMenu = () => {
  // URLs CV FR/EN (Supabase si dispo)
  const cvUrlFr = getCvUrlFor('fr');
  const cvUrlEn = getCvUrlFor('en');

    return (
      <div
        ref={menuRef}
        className={styles.menuContainerOpen}
        role="dialog"
        aria-modal="true"
        aria-label={t('accessibility.mobileMenu')}
      >
        <button
          className={styles.closeButton}
          onClick={closeMobileMenu}
          aria-label={t('accessibility.closeMenu')}
        >
          <MdClose size="1.5rem" aria-hidden="true" />
        </button>

        <nav aria-label={t('accessibility.navigation')}>
          <ul className={styles.mobileMenuList}>
            {/* Liens de navigation mobile */}
            {navItems.map(({ href, key }) => (
              <li key={key}>
                {href.startsWith('/') ? (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(href);
                      closeMobileMenu();
                    }}
                    className={styles.mobileMenuItem}
                  >
                    {t(key)}
                  </a>
                ) : (
                  <a
                    href={href}
                    onClick={href.startsWith('mailto:') ? undefined : closeMobileMenu}
                    className={styles.mobileMenuItem}
                    {...(href.startsWith('mailto:') ? { target: '_self' } : {})}
                  >
                    {t(key)}
                  </a>
                )}
              </li>
            ))}

            {/* Contrôles mobile */}
            <li className={styles.mobileControls}>
              <ThemeToggle />
            </li>
            <li className={styles.mobileControls}>
              <LanguageToggle />
            </li>

            {/* Téléchargement CV mobile (un seul bouton selon la langue) */}
            {language === 'en' ? (
              <li>
                <a
                  href={cvUrlEn}
                  download
                  className={styles.mobileMenuItem}
                  aria-label={t('menu.downloadCV')}
                >
                  Resume
                  <MdFileDownload size="1.2rem" style={{ verticalAlign: 'middle', marginLeft: '8px' }} aria-hidden="true" />
                </a>
              </li>
            ) : (
              <li>
                <a
                  href={cvUrlFr}
                  download
                  className={styles.mobileMenuItem}
                  aria-label={t('menu.downloadCV')}
                >
                  CV
                  <MdFileDownload size="1.2rem" style={{ verticalAlign: 'middle', marginLeft: '8px' }} aria-hidden="true" />
                </a>
              </li>
            )}
          </ul>
        </nav>
      </div>
    );
  }

  // ================================
  // display DE loading
  // ================================

  if (logosLoading) {
    return (
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.logoWrapper}>
            <div className={styles.logoLoading}>
              <div className={styles.logoSkeleton}></div>
              <span>Chargement...</span>
            </div>
          </div>
        </div>
      </header>
    );
  }

  // ================================
  // rendering PRINCIPAL
  // ================================

  return (
    <header className={styles.header}>

      {/* ================================ */}
      {/* link D'ACCESSIBILITÉ */}
      {/* ================================ */}

      <a href="#main-content" className={styles.skipLink}>
        {t('accessibility.skipToContent')}
      </a>

      {/* ================================ */}
      {/* OVERLAY ET MENU MOBILE (PORTALS) */}
      {/* ================================ */}

      {/* Overlay pour fermer le menu */}
      {isMenuOpen && createPortal(
        <div className={styles.overlay} onClick={closeMobileMenu} />,
        document.body
      )}

      {/* Menu mobile via portal */}
      {isMenuOpen && createPortal(
        renderMobileMenu(),
        document.body
      )}

      {/* ================================ */}
      {/* CONTENU PRINCIPAL DU HEADER */}
      {/* ================================ */}

      <div className={styles.headerInner}>

        {/* section logo et title */}
        <div className={styles.logoWrapper}>
          <a
            href="/"
            className={`${styles.logo}`}
            aria-label="Accueil - Jean-François Chaussoy"
            onClick={handleLogoClick}
            title={'Accueil'}
          >
            {/* ✅ Logo DYNAMIQUE */}
            <img
              src={getCurrentLogo()}
              alt="JFC WebCraft Logo"
              className={styles.logoImg}
              width={isMobile ? "200" : "700"}
              height={isMobile ? "120" : "200"}
              onError={handleLogoError}
            />
          </a>

          {/* Indicateur de progression retiré (aucun indicateur visuel) */}

          {/* title H1 positionné visuellement pour être compatible avec le layout */}
          <h1 className={styles.mainTitle}>
            Jean-François Chaussoy | Full Stack Web Developer & Data Scientist
          </h1>
        </div>

        {/* ================================ */}
        {/* navigation PRINCIPALE */}
        {/* ================================ */}

        <nav className={styles.nav} aria-label={t('accessibility.mainNavigation')}>

          {/* button burger pour mobile */}
          <button
            ref={burgerRef}
            className={styles.burger}
            aria-label={t('accessibility.toggleMenu')}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <MdMenu size="1.5rem" aria-hidden="true" />
          </button>

          {/* Menu desktop */}
          <div className={styles.menuContainer}>
            {renderDesktopNavigation()}
          </div>

        </nav>
      </div>
    </header>
  );
};

export default Header;
